import { useCallback, useMemo } from 'react';
import { useLang } from 'hooks/lang';

import { LoadResults } from 'entities';

import { Column, Label, Button, Row } from 'components/Blocks';

import { listGet, sortByName } from 'utils/array';
import { getID } from 'utils/id';

import { CreateProjectDispatchAction, ProjectCreationProps } from '../types';

interface ServiceCountProps {
  children?: never;
  dispatch: CreateProjectDispatchAction;
  state: ProjectCreationProps;
  data: LoadResults['teams.allServices'];
}

const MIN = 1;
const MAX = 10;

type ServiceCountMap = Record<string, { service: string; count: number; name: string }>;

export function ServiceCount({ dispatch, state, data }: ServiceCountProps): JSX.Element {
  const { t } = useLang();

  const serviceCounts = useMemo(() => {
    const counts = state.services.reduce(
      (acc, { service, name }) => ({
        ...acc,
        [service]: {
          name,
          service,
          count: (acc[service]?.count || 0) + 1,
        },
      }),
      {} as ServiceCountMap,
    );

    return sortByName(Object.values(counts));
  }, [state.services]);

  const addService = useCallback(
    (service: string) => {
      dispatch({
        services: ({ services, countries }) => {
          const serviceBase = listGet(services, service, 'service')!;

          return sortByName([
            ...services,
            {
              ...serviceBase,
              id: getID(),
              costs: Object.fromEntries(
                countries.map((country) => [
                  country,
                  serviceBase.baseCosts.map((cost) => ({ ...cost, value: '' })),
                ]),
              ),
            },
          ]);
        },
      });
    },
    [dispatch],
  );

  const removeService = useCallback(
    (service: string) => {
      dispatch({
        services: ({ services }) => {
          const [, ...remaining] = services.filter((svs) => svs.service === service).reverse();

          if (!remaining.length) return services;

          return sortByName([...services.filter((svs) => svs.service !== service), ...remaining]);
        },
      });
    },
    [dispatch],
  );

  const getEnrichedServiceName = (serviceId: string) => {
    const service = data.find(({ id }) => id === serviceId);

    if (!service) return 'Loading...';

    return (
      <span className="text-sm font-medium">
        <strong>{service.team.name}</strong> {service.name}
      </span>
    );
  };

  return (
    <Column>
      <Label>{t('service-counts')}</Label>

      <div className="grid grid-cols-2 gap-4 small_tablet:grid-cols-1">
        {serviceCounts.map(({ service, count }) => (
          <Row
            className="rounded-md border p-2"
            justifyContent="space-between"
            key={`${service}-count-row`}
          >
            {getEnrichedServiceName(service)}

            <Row className="rounded-full border">
              <Button
                format="square"
                round
                disabled={count === MIN}
                type="button"
                className="h-7 w-7 shrink-0"
                onClick={() => removeService(service)}
              >
                -
              </Button>

              <span>{count}</span>

              <Button
                format="square"
                round
                disabled={count === MAX}
                type="button"
                className="h-7 w-7 shrink-0"
                onClick={() => addService(service)}
              >
                +
              </Button>
            </Row>
          </Row>
        ))}
      </div>
    </Column>
  );
}
